import { useEffect, useRef, useState } from 'react';

interface Options {
  threshold?: number;
  rootMargin?: string;
  delay?: number;
}

const useSectionAnimation = (options: Options = {}) => {
  const { threshold = 0.1, rootMargin = '0px 0px -60px 0px', delay = 0 } = options;
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setVisible(true), delay);
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin, delay]);

  const fadeUp = {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0px)' : 'translateY(36px)',
    transition: 'opacity 0.65s ease, transform 0.65s ease',
  };

  const fadeIn = {
    opacity: visible ? 1 : 0,
    transition: 'opacity 0.65s ease',
  };

  const slideRight = {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateX(0px)' : 'translateX(-32px)',
    transition: 'opacity 0.65s ease, transform 0.65s ease',
  };

  const stagger = (index: number, base = 100) => ({
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0px)' : 'translateY(28px)',
    transition: `opacity 0.55s ease ${index * base}ms, transform 0.55s ease ${index * base}ms`,
  });

  return { ref, visible, fadeUp, fadeIn, slideRight, stagger };
};

export default useSectionAnimation;
